/**
 * Session auto-save service.
 *
 * Subscribes to workspace store changes and automatically saves the session
 * with a debounce to prevent excessive writes during rapid changes (e.g., typing).
 *
 * This ensures session data survives app crashes, not just clean shutdowns.
 */
import { useWorkspaceStore } from '../../workspace/core/store/workspaceStore';
import { flushPendingBuffers } from '../../workspace/core/store/pendingBufferFlushes';
import { useConnectionStore } from '../../connections/store/connectionStore';
import { useSessionStore } from '../store/sessionStore';

// Only suppress logs in unit tests (vitest), not E2E tests (Playwright in Electron)
function isUnitTest(): boolean {
  if (typeof process === 'undefined' || process.env.NODE_ENV !== 'test') {
    return false;
  }
  if (typeof window === 'undefined') {
    return true;
  }
  return !window.electronAPI;
}

/** Debounce delay for auto-save (ms) */
const AUTO_SAVE_DEBOUNCE_MS = 500;

let debounceTimer: ReturnType<typeof setTimeout> | null = null;
let unsubscribeWorkspace: (() => void) | null = null;
let unsubscribeConnections: (() => void) | null = null;
let cleanupFn: (() => void) | null = null;

/**
 * Nesting depth of auto-save pauses.
 *
 * A counter rather than a flag because the pauses genuinely nest: a session
 * SWITCH pauses for the whole teardown→load window, and the validation dialog
 * that runs inside it pauses again for its own lifetime. With a flag, the
 * dialog unmounting would resume auto-save while the workspace is still torn
 * down — the very state the outer pause exists to protect.
 */
let pauseDepth = 0;

/**
 * Whether a session switch currently holds one of those pauses.
 *
 * Lives here, next to `pauseDepth`, and NOT in `sessionStore` — the two are one
 * piece of state and splitting them made `resetSessionAutoSave()` a half-reset:
 * a test that ended mid-switch leaked "held" into the next one, whose
 * `pauseAutoSaveForSessionSwitch()` then early-returned and took no pause at
 * all. Anything that reasons about "is a switch holding auto-save down" must
 * be resettable in one place.
 */
let sessionSwitchPauseHeld = false;

/**
 * Debounced save function.
 * Waits for AUTO_SAVE_DEBOUNCE_MS of inactivity before saving.
 */
function debouncedSave(): void {
  // Don't schedule saves when paused (e.g., during session validation)
  if (pauseDepth > 0) {
    return;
  }

  if (debounceTimer) {
    clearTimeout(debounceTimer);
  }

  debounceTimer = setTimeout(() => {
    // Double-check pause state in case it changed during debounce
    if (pauseDepth > 0) {
      debounceTimer = null;
      return;
    }
    const sessionStore = useSessionStore.getState();
    sessionStore.saveCurrentSession();
    debounceTimer = null;
  }, AUTO_SAVE_DEBOUNCE_MS);
}

/**
 * Initialize session auto-save.
 *
 * Subscribes to workspace store changes and triggers debounced saves.
 * Should be called once during app initialization.
 *
 * @returns Cleanup function to stop auto-save
 */
export function initializeSessionAutoSave(): () => void {
  // Avoid duplicate subscriptions
  if (cleanupFn) {
    console.log('[SessionAutoSave] Already initialized, skipping');
    return cleanupFn;
  }

  if (!isUnitTest()) console.log('[SessionAutoSave] Initializing with debounce:', AUTO_SAVE_DEBOUNCE_MS, 'ms');

  // Subscribe to workspace store changes
  // We track tabs and activeTabId - these are the main things we want to persist
  unsubscribeWorkspace = useWorkspaceStore.subscribe(
    (state, prevState) => {
      // Only save if tabs or activeTabId changed
      const tabsChanged = state.tabs !== prevState.tabs;
      const activeTabChanged = state.activeTabId !== prevState.activeTabId;

      if (tabsChanged || activeTabChanged) {
        debouncedSave();
      }
    }
  );

  // Also persist active connection changes (e.g., user connects/disconnects without opening tabs)
  unsubscribeConnections = useConnectionStore.subscribe((state, prevState) => {
    const activeConnectionsChanged = state.activeConnections !== prevState.activeConnections;
    if (activeConnectionsChanged) {
      debouncedSave();
    }
  });

  cleanupFn = () => {
    if (debounceTimer) {
      clearTimeout(debounceTimer);
      debounceTimer = null;
    }
    if (unsubscribeWorkspace) {
      unsubscribeWorkspace();
      unsubscribeWorkspace = null;
    }
    if (unsubscribeConnections) {
      unsubscribeConnections();
      unsubscribeConnections = null;
    }
    cleanupFn = null;
    if (!isUnitTest()) console.log('[SessionAutoSave] Stopped');
  };

  return cleanupFn;
}

/**
 * Force an immediate save (bypasses debounce).
 * Useful for critical moments like app shutdown.
 */
export function saveSessionImmediately(): void {
  // Editor text still inside its own debounce (a query tab's 200 ms buffer
  // flush) goes into the store first, so the snapshot has the latest typing.
  // The store change this causes re-arms the debounced save; it is cleared
  // just below.
  flushPendingBuffers();
  if (debounceTimer) {
    clearTimeout(debounceTimer);
    debounceTimer = null;
  }
  const sessionStore = useSessionStore.getState();
  sessionStore.saveCurrentSession();
}

/**
 * Cancel any pending debounced auto-save without touching subscriptions.
 * Useful for "clear session" flows where we don't want a queued save to recreate state.
 */
export function cancelPendingSessionAutoSave(): void {
  if (!debounceTimer) return;
  clearTimeout(debounceTimer);
  debounceTimer = null;
}

/**
 * Settle the pending debounced save before a restore replaces the workspace.
 *
 * A restore REPLACES the tab set, so a tab the user opened in the last 500ms —
 * still sitting in the debounce, not yet in any session — would be wiped with
 * no trace. Committing the queued save first hands that work back through the
 * restore instead.
 *
 * It only ever commits work, never its absence: a queued save against a
 * workspace with no meaningful tabs is DROPPED, because writing it would empty
 * the session of the very tabs the restore exists to bring back. Same rule
 * `saveCurrentSession` follows for un-restored sessions, applied one step
 * earlier.
 *
 * A no-op when nothing is queued — which is what makes it safe on every
 * restore path. Inside a paused window (session-switch teardown) no timer can
 * be queued at all, so this can never commit a half-torn-down workspace.
 */
export function flushPendingSessionAutoSaveBeforeRestore(): void {
  if (!debounceTimer) return;
  clearTimeout(debounceTimer);
  debounceTimer = null;

  const hasMeaningfulTabs = useWorkspaceStore
    .getState()
    .tabs.some((tab) => tab.type !== 'start-page');
  if (!hasMeaningfulTabs) return;

  useSessionStore.getState().saveCurrentSession();
}

/**
 * Reset auto-save state (for testing).
 * Cancels any pending saves and clears the subscription.
 */
export function resetSessionAutoSave(): void {
  if (debounceTimer) {
    clearTimeout(debounceTimer);
    debounceTimer = null;
  }
  if (unsubscribeWorkspace) {
    unsubscribeWorkspace();
    unsubscribeWorkspace = null;
  }
  if (unsubscribeConnections) {
    unsubscribeConnections();
    unsubscribeConnections = null;
  }
  cleanupFn = null;
  pauseDepth = 0;
  sessionSwitchPauseHeld = false;
  if (!isUnitTest()) console.log('[SessionAutoSave] Reset');
}

/**
 * Pause auto-save temporarily.
 * Use during operations where session state shouldn't be saved (e.g., session
 * switch teardown, validation). Also cancels any pending debounced save.
 *
 * Pauses nest — every `pauseSessionAutoSave()` needs its own
 * `resumeSessionAutoSave()`.
 *
 * NOTE: `saveSessionImmediately()` deliberately bypasses this — quitting the
 * app must persist the workspace even mid-switch, and a dropped quit-time save
 * would be worse than a torn-down one. What keeps that bypass safe is
 * `saveCurrentSession`'s un-restored-workspace guard, which refuses to write a
 * workspace that never represented the session over its tabs. Do not "simplify"
 * that guard away on the assumption that the pause covers this path.
 */
export function pauseSessionAutoSave(): void {
  pauseDepth += 1;
  // Cancel any pending save to prevent it from firing
  if (debounceTimer) {
    clearTimeout(debounceTimer);
    debounceTimer = null;
  }
  if (!isUnitTest()) console.log('[SessionAutoSave] Paused, depth:', pauseDepth);
}

/**
 * Resume auto-save after being paused. Clamped at zero so an unmatched resume
 * can never push the depth negative and disarm a later pause.
 */
export function resumeSessionAutoSave(): void {
  if (pauseDepth === 0) return;
  pauseDepth -= 1;
  if (!isUnitTest()) console.log('[SessionAutoSave] Resumed, depth:', pauseDepth);
}

/** Whether auto-save is currently paused (test/diagnostic use). */
export function isSessionAutoSavePaused(): boolean {
  return pauseDepth > 0;
}

/**
 * Take the auto-save pause that spans a whole session switch.
 *
 * Switching sessions tears the workspace down (disconnect every connection,
 * then `restoreTabs([], null)`) and only rebuilds it once the incoming session
 * has been validated and loaded — which can wait on a user decision. Every
 * teardown step is a store write the auto-save subscription reacts to, so a
 * debounce landing anywhere in that window used to serialize a half-torn-down
 * workspace over the session (the "renamed to Untitled / lost all its tabs"
 * report).
 *
 * Idempotent: a second call while a switch already holds the pause is a no-op,
 * so the window is one pause no matter how the flow re-enters.
 */
export function pauseAutoSaveForSessionSwitch(): void {
  if (sessionSwitchPauseHeld) return;
  sessionSwitchPauseHeld = true;
  pauseSessionAutoSave();
}

/**
 * Release the switch's pause. Called by every exit — load finished, teardown
 * threw, or the user cancelled validation. A window that never closes means
 * auto-save is off for the rest of the app run, i.e. silent data loss, so treat
 * the pairing as load-bearing. No-op when no switch holds the pause.
 */
export function releaseSessionSwitchPause(): void {
  if (!sessionSwitchPauseHeld) return;
  sessionSwitchPauseHeld = false;
  resumeSessionAutoSave();
}
